<script context="module" lang="ts">
  import { writable } from 'svelte/store';
  const activeContextMenuId = writable<string | null>(null);
</script>

<script lang="ts">
  import { Activity, type Exercise } from '@/Common/ActivityFunctions';
  import { programTypeIcon } from '@/Common/constants';
  import Matchstick from '@/Common/Matchstick.svelte';
  import { cn } from '@/lib/utils';
  import { getContext, onMount } from 'svelte';
  import type { MyFitnessStores } from '../stores';
  import { convertSeconds, kmToMile, StrToProgramType, tidy } from '@/Util/utils';
  import XSSCircle from '../TrainingForecast/XSSCircle.svelte';
  import { Skeleton } from '@/lib/components/ui/skeleton';
  import { format } from 'date-fns';
  import DifficultyDiamonds from '@/DifficultyDiamonds.svelte';
  import ExerciseDialog from '../Today/ExerciseDialog.svelte';
  import * as ContextMenu from '$lib/components/ui/context-menu/index.js';
  import { TagWrapper } from '@/Common';
  import { ActivityTag } from '@/Common/ActivityTag';
  import CalendarActivityCardActionsContextMenu from './CalendarActivityCardActionsContextMenu.svelte';
  import { CalendarLoader } from './CalendarLoaderNew';
  import { draggable, dragInformation, DragType, dropzone } from '@/Common/DragAndDrop';
  import { swapCalendarEvents, importAndAssignWorkout } from './CalendarEventUtils';
  import { CreateCustomEventDispatchHandler } from '@/EventDispatchers';

  export let activity: Exercise;
  export let isStale: boolean = false; // indicates if the activity data is stale. All interaction should be prevented, this is like a loading state
  export let conflictDay: boolean = false; // TODO (hyde): indicates if a forecast activity is in a conflicting state
  export let layoutType: 'vertical' | 'horizontal' = 'vertical'; // determines whether the image is rendered above the content or to the right of the content
  export let onDrag: (x: number, y: number) => void;
  export let calendarLoader: CalendarLoader;

  //@ts-ignore
  const Xert = window.Xert;
  const myFitness = getContext<MyFitnessStores>('myFitness');
  const { trainingPlan } = myFitness;
  let useFreedomUnits: boolean = Xert._displayUnits == 'imperial';
  let rootDiv: HTMLElement;

  const hoverQuery = window.matchMedia('(hover: hover)');
  let isTouchDevice = !hoverQuery.matches;
  hoverQuery.addEventListener('change', (e) => isTouchDevice = !e.matches);
  
  let activityNumbersContainer: ResizeObserverSize[] | null = null;
  let activityNumbersWidth: number | null = null;
  $: if (activityNumbersContainer && activityNumbersContainer[0]) activityNumbersWidth = activityNumbersContainer[0].inlineSize;
  $: useCompactNumbersLayout = activityNumbersWidth < 300;

  $: programType = StrToProgramType($trainingPlan?.program_type, $trainingPlan?.selected_mode);
  $: isRest = Activity.isRest(activity);
  $: isRecorded = Activity.isRecorded(activity);
  $: imageUrl = Activity.getOneImage(activity)?.url;
  // The Magic Buckets icon is square: it fills the space of a workout image (354x200) so the card isn't taller for it, with the
  // icon a little smaller inside, softened and on a pale blue background to sit with the tones of the workout charts.
  $: imageClass = Activity.isMagicBuckets(activity) ? 'aspect-[354/200] object-contain p-3 bg-gradient-to-b from-sky-50 to-sky-100 saturate-[.6] opacity-80' : '';
  $: isGoalEvent = Activity.isGoalEvent(activity);
  $: isPlaceholder = Activity.isPlaceholder(activity);
  $: isForecastEvent = Activity.isForecastEvent(activity);
  $: isPlanned = Activity.isPlanned(activity);
  $: canDrag = !isRecorded && !isGoalEvent;

  $: canDrop = $dragInformation != null && (($dragInformation.type == DragType.SWAP && isForecastEvent) || ($dragInformation.type == DragType.IMPORT && isPlaceholder))
  $: dropDisabled = $dragInformation != null && !(($dragInformation.type == DragType.SWAP && isForecastEvent) || ($dragInformation.type == DragType.IMPORT && isPlaceholder))

  // TODO (hyde): use smarter xss details for the XSSCircle
  $: XSSDetails = {
    xss: activity.xss ?? 0, // Non-power activities have undefined xss
    xlss: activity.xlss ?? 0,
    xhss: activity.xhss ?? 0,
    xpss: activity.xpss ?? 0,
  };
  $: forecastedXSSDetails = activity.placeholder_xss_details
    ? {
        xss: activity.placeholder_xss_details.xss ?? 0,
        xlss: activity.placeholder_xss_details.xlss ?? 0,
        xhss: activity.placeholder_xss_details.xhss ?? 0,
        xpss: activity.placeholder_xss_details.xpss ?? 0,
      }
    : null;

  let detailsDialogOpen: boolean = false;

  // This is needed so that only one right-click context menu can be open at a time.
  // Otherwise, you can right click on each card and have multiple context menus open, which looks bad and can cause bugs.
  $: myContextMenuId = String(activity.path ?? activity.start_date);
  let contextMenuOpen = false;
  let contextMenuTriggerEl: HTMLElement;
  // Close this menu when a different card's context menu opens
  $: if ($activeContextMenuId !== myContextMenuId && contextMenuOpen) {
    contextMenuOpen = false;
  }

  function openContextMenu(e: MouseEvent) {
    contextMenuTriggerEl?.dispatchEvent(new MouseEvent('contextmenu', { bubbles: true, clientX: e.clientX, clientY: e.clientY }));
  }

  const activityTag = ActivityTag('small');
  $: activityTags = [
    activityTag.pinned(activity),
    activityTag.forecast(activity, isRest),
    !isForecastEvent ? activityTag.planned(activity) : null,
    isPlanned && !isPlaceholder ? activityTag.exerciseType(activity) : null, 
    activityTag.device(activity),
    activityTag.strava(activity),
    activityTag.powerSource(activity),
    activityTag.merged(activity),
    activityTag.breakthrough(activity),
    activityTag.manual(activity),
  ].filter((tag) => tag);

  // TODO (hyde): don't do this to open the edit modal, this is bad old code
  let eventDispatchHandler;
  onMount(()=>{
    //Returns custom Event Dispatchers that can be used directly as event handlers
    //e.g. on:click={eventDispatcherHandler('myEvent', {...myEventDetails}, bubbles)}

    //Dispatches Custom Events that can be used within component methods
    //e.g. function myMethod() {/*modify component before call*/...; dispatchTrainingEvent('myEvent', {...myEventDetails}, bubbles); /*modify component after call*/  } 
    [eventDispatchHandler] = CreateCustomEventDispatchHandler(rootDiv);
  })

</script>

<!-- svelte-ignore a11y-click-events-have-key-events -->
<!-- svelte-ignore a11y-no-static-element-interactions -->
<div
  bind:this={rootDiv}
  class={cn('calendar-activity-card w-full flex flex-row gap-0 rounded-sm', 'border border-border cursor-pointer clickable', isRest ? 'bg-none border-dashed' : 'bg-muted', canDrag ? 'cursor-grab' : '')}
  on:click={() => {
    if (isPlaceholder && !isGoalEvent) eventDispatchHandler("editEvent", {date: new Date(activity.start_date), exercise: activity})()
    else if (!isRest) detailsDialogOpen = true;
  }}
  class:dropDisabled={dropDisabled}
  class:canDrop={canDrop}
  use:draggable={{
    disabled: !canDrag || isTouchDevice,
    data: { exercise: activity, operation: isForecastEvent ? DragType.SWAP : DragType.MOVE },
    image: rootDiv,
    on_dragstart(operation) {
      rootDiv?.classList.add('dragged');
      dragInformation.set({
        type: operation,
      });
    },
    on_dragend() {
      rootDiv?.classList.remove('dragged');
    },
    onDrag,
  }}
  use:dropzone={{
    on_dropzone(data, operation) {
      if (operation == 'swap' && isForecastEvent) {
        let draggedExercise = data.exercise;
        swapCalendarEvents(
          draggedExercise,
          activity,
          () => calendarLoader.swapActivities(draggedExercise, activity),
          Xert,
        );
      } else if (operation == 'import' && isPlaceholder) {
        importAndAssignWorkout(data, activity, () => {}, (date) => calendarLoader.reloadFrom(date), Xert)

      }
    },
  }}
>
  <Matchstick width={4} {conflictDay} xss={activity.xss} xlss={activity.xlss} xpss={activity.xpss} xhss={activity.xhss} {isRest} />
  <ContextMenu.Root
    bind:open={contextMenuOpen}
    onOpenChange={(open) => { if (open) activeContextMenuId.set(myContextMenuId); }}
    portal="#shadcn-ui-dialog-target"
  >
    <ContextMenu.Trigger class='flex-1 min-w-0'>
      <div bind:this={contextMenuTriggerEl} class={cn('calendar-activity-card-content w-full flex flex-row items-center gap-2', layoutType == 'horizontal' ? 'pr-2' : '')}>
        <div class="flex flex-col gap-1 p-1 flex-1 min-w-0">
          {#if imageUrl && layoutType == 'vertical'}
            <div class="relative">
              <img class={cn("rounded-md border border-border w-full", imageClass)} src={imageUrl} alt="Chart" draggable="false" on:error={() => (imageUrl = null)} />
              {#if !isGoalEvent}
                <button
                  class={cn("absolute -top-1 -right-1 z-10 w-6 h-6 flex items-center justify-center rounded-sm text-black hover:bg-black/30 border-0", isRest ? 'bg-none bg-transparent' : 'bg-muted')}
                  on:click|stopPropagation={openContextMenu}
                >
                  <i class="fa-regular fa-bars text-sm" />
                </button>
              {/if}
            </div>
          {/if}
          <div class="flex flex-row gap-1 items-center">
            {#if isGoalEvent}
              <i class={cn('fa-light text-black mr-1', programTypeIcon[programType])} />
            {:else if isRest}
              <i class="fa-light fa-sheep text-black"></i>
            {:else if activity.type}
              {@const typeLower = activity.type.toLowerCase()}
              {#if typeLower == 'ride' || typeLower == 'cycling' || typeLower == 'virtualride'}
                <i class="fa-light fa-bicycle text-black"></i>
              {:else if typeLower == 'run' || typeLower == 'running' || typeLower == 'hike' || typeLower == 'virtualrun'}
                <i class="fa-light fa-walking text-black"></i>
              {:else if typeLower == 'swim' || typeLower == 'swimming'}
                <i class="fa-light fa-swimmer text-black"></i>
              {/if}
            {/if}
            <h3 class={cn("font-semibold m-0 line-clamp-2 flex-1", layoutType == 'horizontal' ? 'text-base' : 'text-xs')}>
              {activity.name}
            </h3>
            {#if !imageUrl && !isGoalEvent}
              <button
                class={cn("self-start -mt-1 -mr-1 z-10 w-6 h-6 flex items-center justify-center rounded-sm text-black hover:bg-black/30 border-0", isRest ? 'bg-none bg-transparent' : 'bg-muted')}
                on:click|stopPropagation={openContextMenu}
              >
                <i class="fa-regular fa-bars text-sm" />
              </button>
            {/if}
          </div>
          <TagWrapper tags={activityTags} />
          <!-- {#if activity.focus ?? activity.fa}
            <span class="text-[10px] font-medium">{activity.spr} {activity.focus ?? activity.fa}</span>
          {/if} -->
          {#if !isRest && !isGoalEvent}
            <div class="flex flex-row items-center gap-2">
              <XSSCircle loading={isStale} primaryXSSDetails={XSSDetails} primaryTitle={isPlaceholder ? 'Forecasted' : Activity.isPlanned(activity) ? 'Scheduled' : 'Completed'} targetXSSDetails={forecastedXSSDetails} secondaryTitle={Activity.isPlanned(activity) ? 'Forecasted' : 'Scheduled'} focus={isPlaceholder ? (activity.focus ?? activity.fa) : null} specRating={isPlaceholder ? activity.spr : null} />
              <div bind:borderBoxSize={activityNumbersContainer} class={cn('flex-1 min-w-0 max-w-[300px] flex', useCompactNumbersLayout ? 'flex-col items-end text-right' : 'flex-row gap-4')}>
                {#if useCompactNumbersLayout}
                  {#if activity.distance}
                    {#if isStale}
                      <Skeleton class="h-[11px] w-[50px]" />
                    {:else}
                      <span class="text-[10px] font-medium">{tidy(useFreedomUnits ? kmToMile(activity.distance) : activity.distance)} {useFreedomUnits ? 'mi' : 'km'}</span>
                    {/if}
                  {/if}
                  {#if isStale}
                    <Skeleton class="h-[11px] w-[30px]" />
                  {:else}
                    <span class="text-[10px] font-medium">{convertSeconds(activity.duration ?? activity.adu)}</span>
                  {/if}
                  {#if isStale}
                    <Skeleton class="h-[11px] w-[40px]" />
                  {:else}
                    <span class="text-[10px] font-medium">{format(new Date(activity.start_date), 'hh:mm aa')}</span>
                  {/if}
                  {#if activity.difficulty}
                    <DifficultyDiamonds difficulty={activity.difficulty} compact={true} />
                  {/if}
                {:else}
                  <div class="flex flex-col gap-0">
                    <span class="text-[10px] font-medium uppercase text-muted-foreground">Duration</span>
                    {#if isStale}
                      <Skeleton class="h-[11px] w-[30px]" />
                    {:else}
                      <span class="text-[14px] font-medium">{convertSeconds(activity.duration ?? activity.adu)}</span>
                    {/if}
                  </div>
                  {#if activity.distance}
                    <div class="flex flex-col gap-0">
                      <span class="text-[10px] font-medium uppercase text-muted-foreground">Distance</span>
                      {#if isStale}
                        <Skeleton class="h-[11px] w-[50px]" />
                      {:else}
                        <span class="text-[14px] font-medium">{tidy(useFreedomUnits ? kmToMile(activity.distance) : activity.distance)} {useFreedomUnits ? 'mi' : 'km'}</span>
                      {/if}
                    </div>
                  {/if}
                  <div class="flex flex-col gap-0">
                    <span class="text-[10px] font-medium uppercase text-muted-foreground">Start Time</span>
                    {#if isStale}
                      <Skeleton class="h-[11px] w-[40px]" />
                    {:else}
                      <span class="text-[14px] font-medium">{format(new Date(activity.start_date), 'hh:mm aa')}</span>
                    {/if}
                  </div>
                  {#if activity.difficulty}
                    <div class="flex flex-col gap-0">
                      <span class="text-[10px] font-medium uppercase text-muted-foreground">Difficulty</span>
                      <DifficultyDiamonds difficulty={activity.difficulty} compact={useCompactNumbersLayout} />
                    </div>
                  {/if}
                {/if}
              </div>
            </div>
          {/if}
        </div>
        {#if imageUrl && layoutType == 'horizontal'}
          <div class="relative self-stretch flex flex-row items-center">
            <img class={cn("rounded-md border border-border w-[150px] h-max", imageClass)} src={imageUrl} alt="Chart" draggable="false" on:error={() => (imageUrl = null)} />
            {#if !isGoalEvent}
              <button
                class={cn("absolute top-0 right-0 z-10 w-6 h-6 flex items-center justify-center rounded-sm text-black hover:bg-black/30 border-0", isRest ? 'bg-none bg-transparent' : 'bg-muted')}
                on:click|stopPropagation={openContextMenu}
              >
                <i class="fa-regular fa-bars text-sm" />
              </button>
            {/if}
          </div>
        {/if}
      </div>
    </ContextMenu.Trigger>
    <ContextMenu.Content>
      <CalendarActivityCardActionsContextMenu bind:open={contextMenuOpen} {activity} disabled={isStale} {calendarLoader} {rootDiv} />
    </ContextMenu.Content>
  </ContextMenu.Root>
</div>

<ExerciseDialog bind:open={detailsDialogOpen} exercise={activity} hideScheduleButtons {calendarLoader} {rootDiv} />

<style lang="scss">
  .canDrop {
    box-shadow: inset 0 0 10px #7ed32199;
  }

  .calendar-activity-card:global(.droppable) {
    opacity: 0.5;
  }
  .calendar-activity-card.canDrop :global(*) {
    pointer-events: none !important;
  }

  .dropDisabled:not(.dragged) {
    pointer-events: none;
  }
  .calendar-activity-card:not(.dropDisabled) {
    pointer-events: auto;
  }

  .calendar-activity-card.clickable {
    transition: transform 150ms ease, box-shadow 150ms ease;

    &:hover {
      transform: translateY(-1px);
      box-shadow: 0 3px 8px rgba(0, 0, 0, 0.12);
    }

    &:active {
      transform: translateY(0);
      box-shadow: none;
    }
  }
</style>
